import type { Material, Mesh, Object3D, Texture } from 'three/webgpu';

const TEXTURE_SLOTS = [
  'map',
  'normalMap',
  'roughnessMap',
  'metalnessMap',
  'aoMap',
  'emissiveMap',
  'alphaMap',
] as const;

function isMesh(o: Object3D): o is Mesh {
  return (o as Mesh).isMesh === true;
}

export function forEachMaterial(o: Object3D, fn: (m: Material) => void): void {
  if (!isMesh(o)) return;
  const mat = o.material;
  if (Array.isArray(mat)) mat.forEach(fn);
  else fn(mat);
}

export function forEachTexture(m: Material, fn: (t: Texture) => void): void {
  const record = m as unknown as Record<string, unknown>;
  for (const slot of TEXTURE_SLOTS) {
    const value = record[slot];
    if (value && typeof value === 'object' && (value as Texture).isTexture) fn(value as Texture);
  }
}

/** Frees geometries, materials, and their textures for a loaded subtree. */
export function disposeObject(root: Object3D): void {
  const materials = new Set<Material>();
  root.traverse((o) => {
    if (isMesh(o)) {
      o.geometry.dispose();
      forEachMaterial(o, (m) => materials.add(m));
    }
  });
  for (const m of materials) {
    forEachTexture(m, (t) => t.dispose());
    m.dispose();
  }
}

export function setAnisotropy(root: Object3D, anisotropy: number): void {
  root.traverse((o) =>
    forEachMaterial(o, (m) =>
      forEachTexture(m, (t) => {
        if (t.anisotropy !== anisotropy) {
          t.anisotropy = anisotropy;
          t.needsUpdate = true;
        }
      }),
    ),
  );
}
